import { useEffect, useState } from 'react';
import { FaBoxes, FaChartLine, FaCheckCircle, FaClipboardList, FaUsers } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../utils/apiClient';

const statsConfig = [
  { titleKey: 'dashboard.overview.stats.availableProducts', noteKey: 'dashboard.overview.stats.readyInInventory', key: 'available_products', icon: FaBoxes, accent: 'bg-emerald-50 text-emerald-700' },
  { titleKey: 'dashboard.overview.stats.customersWaitlist', noteKey: 'dashboard.overview.stats.waitingForRestock', key: 'customer_waitlist', icon: FaUsers, accent: 'bg-sky-50 text-sky-700' },
  { titleKey: 'dashboard.overview.stats.requiredProducts', noteKey: 'dashboard.overview.stats.needAttention', key: 'required_products', icon: FaClipboardList, accent: 'bg-amber-50 text-amber-700' },
  { titleKey: 'dashboard.overview.stats.completedOrders', noteKey: 'dashboard.overview.stats.successfullyDelivered', key: 'completed_orders', icon: FaCheckCircle, accent: 'bg-teal-50 text-teal-700' },
];

const Statistics = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    available_products: 0,
    customer_waitlist: 0,
    required_products: 0,
    completed_orders: 0,
  });

  useEffect(() => {
    apiClient.get('/stats')
      .then((response) => setStats(response.data || {}))
      .catch(() => setStats({
        available_products: 0,
        customer_waitlist: 0,
        required_products: 0,
        completed_orders: 0,
      }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="lg:col-span-10 md:col-span-9">
      <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4 shadow-sm md:p-6">
        <div className="mb-6 flex flex-col justify-between gap-4 rounded-lg bg-white p-5 text-left shadow-sm md:flex-row md:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">{t('dashboard.overview.label')}</p>
            <h2 className="mt-2 text-3xl font-semibold text-[#00595A]">{t('dashboard.overview.title')}</h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">{t('dashboard.overview.subtitle')}</p>
          </div>
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-[#00595A] text-white shadow-sm">
            <FaChartLine className="h-6 w-6" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {loading
            ? statsConfig.map(({ key }) => (
                <article key={key} className="rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="w-full">
                      <div className="h-4 w-28 animate-pulse rounded bg-slate-200" />
                      <div className="mt-3 h-10 w-20 animate-pulse rounded bg-slate-200" />
                    </div>
                    <div className="h-11 w-11 animate-pulse rounded bg-slate-200" />
                  </div>
                  <div className="mt-5 h-2 overflow-hidden rounded bg-slate-100">
                    <div className="h-full w-3/4 rounded bg-slate-200" />
                  </div>
                  <div className="mt-3 h-4 w-36 animate-pulse rounded bg-slate-200" />
                </article>
              ))
            : statsConfig.map(({ titleKey, noteKey, key, icon: Icon, accent }) => (
                <article key={key} className="rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-md">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-slate-500">{t(titleKey)}</p>
                      <h3 className="mt-3 text-4xl font-semibold text-slate-900">{stats[key]}</h3>
                    </div>
                    <span className={`grid h-11 w-11 shrink-0 place-items-center rounded ${accent}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="mt-5 h-2 overflow-hidden rounded bg-slate-100">
                    <div className="h-full w-3/4 rounded bg-[#00595A]" />
                  </div>
                  <p className="mt-3 text-sm text-slate-500">{t(noteKey)}</p>
                </article>
              ))}
        </div>
      </div>
    </section>
  );
};

export default Statistics;
